import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Checkbox, Select, Tooltip } from 'antd';
import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { PRIORITY_TONE, getTaskPriorityLabel } from '../../tasks/lib/taskMeta.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import { formatDateTime, taskDueState } from '../../../shared/format/entity-detail.js';
import type { TaskItem, TaskStatus } from '../../tasks/api/tasks.client.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { LinkCell } from '../../../shared/ui/LinkCell.js';

export function ContactTaskList({
  tasks,
  pending,
  onToggle,
  onStatus,
  onEdit,
  onHistory,
  onDelete,
  deleting,
  filter,
  action,
  loading,
  error,
  currentDealId,
  currentCompanyId,
}: {
  tasks: TaskItem[];
  pending: boolean;
  onToggle: (task: TaskItem) => void;
  onStatus: (id: string, status: TaskStatus) => void;
  onEdit: (task: TaskItem) => void;
  onHistory: (task: TaskItem) => void;
  onDelete: (id: string) => Promise<unknown>;
  deleting: boolean;
  filter?: ReactNode;
  action?: ReactNode;
  loading?: boolean;
  error?: Error | null;
  currentDealId?: string;
  currentCompanyId?: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <EntityRelatedList
      filter={filter}
      action={action}
      loading={loading ?? false}
      error={error ?? null}
      emptyText={t('crm.entity_detail.empty_tasks')}
      rows={tasks.map((task) => ({
        id: task.id,
        className: 'entity-task-row',
        title: task.title,
        href: `/tasks/${task.id}`,
        leading: (
          <Tooltip
            title={t(
              task.status === 'done'
                ? 'crm.entity_detail.reopen_task'
                : 'crm.entity_detail.complete_task',
              { title: task.title },
            )}
          >
            <Checkbox
              className="entity-task-checkbox"
              checked={task.status === 'done'}
              disabled={pending}
              aria-label={t(
                task.status === 'done'
                  ? 'crm.entity_detail.reopen_task'
                  : 'crm.entity_detail.complete_task',
                { title: task.title },
              )}
              onChange={() => onToggle(task)}
            />
          </Tooltip>
        ),
        meta: (
          <>
            {task.assigneeMember?.user.name ?? task.assigneeMember?.user.email}
            {currentCompanyId ? (
              <>
                {task.deal || task.inheritedLinks?.deal ? (
                  <>
                    {task.assigneeMember ? ' · ' : null}
                    <LinkCell
                      kind="deal"
                      own={task.deal ? { id: task.deal.id, label: task.deal.title } : null}
                      inherited={task.inheritedLinks?.deal}
                    />
                  </>
                ) : null}
                {task.contact || task.inheritedLinks?.contact ? (
                  <>
                    {task.assigneeMember || task.deal || task.inheritedLinks?.deal ? ' · ' : null}
                    <LinkCell
                      kind="contact"
                      own={
                        task.contact
                          ? {
                              id: task.contact.id,
                              label: formatPersonName(
                                task.contact.firstName,
                                task.contact.lastName,
                              ),
                            }
                          : null
                      }
                      inherited={task.inheritedLinks?.contact}
                    />
                  </>
                ) : null}
              </>
            ) : task.deal && task.deal.id !== currentDealId ? (
              <>
                {task.assigneeMember ? ' · ' : null}
                <Link to={`/deals/${task.deal.id}`}>{task.deal.title}</Link>
              </>
            ) : null}
          </>
        ),
        status: (
          <span className="entity-task-status">
            <EntityStatusChip tone={PRIORITY_TONE[task.priority]} className="entity-status-chip">
              {getTaskPriorityLabel(task.priority)}
            </EntityStatusChip>
            <Select
              data-testid={`task-status-${task.id}`}
              aria-label={t('crm.entity_detail.task_status', { title: task.title })}
              size="small"
              variant="borderless"
              onClick={(event) => event.stopPropagation()}
              value={task.status}
              disabled={pending}
              style={{ minWidth: 130 }}
              options={(['todo', 'in_progress', 'blocked', 'done', 'cancelled'] as const).map(
                (value) => ({ value, label: t(`crm.entity_detail.task_${value}`) }),
              )}
              onChange={(value: TaskStatus) => onStatus(task.id, value)}
            />
          </span>
        ),
        value: task.dueAt ? (
          <div className="entity-task-due">
            <time dateTime={task.dueAt}>{formatDateTime(task.dueAt)}</time>
            {task.status !== 'done' &&
            task.status !== 'cancelled' &&
            taskDueState(task.dueAt) === 'overdue' ? (
              <EntityTaskDueChip dueAt={task.dueAt} />
            ) : null}
          </div>
        ) : null,
        visibleActions: (
          <div className="entity-inline-actions">
            <Tooltip title={t('crm.entity_detail.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label={t('crm.entity_detail.edit')}
                onClick={() => onEdit(task)}
              />
            </Tooltip>
            <Tooltip title={t('crm.entity_detail.history')}>
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                aria-label={t('crm.entity_detail.history')}
                onClick={() => onHistory(task)}
              />
            </Tooltip>
          </div>
        ),
        actions: [
          {
            key: 'delete',
            label: t('crm.entity_detail.delete'),
            danger: true,
            disabled: deleting,
            confirm: { title: t('crm.entity_detail.delete_confirm') },
            onClick: () => onDelete(task.id),
          },
        ],
      }))}
    />
  );
}
